import { asyncScheduler, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { endOfDay, startOfDay, subDays } from 'date-fns';
import curry from 'lodash/curry';
import lodashReplace from 'lodash/replace';
import lodashMap from 'lodash/map';
import lodashSortBy from 'lodash/sortBy';
import clamp from 'lodash/clamp';
import forOwn from 'lodash/forOwn';
import compact from 'lodash/compact';
import flow from 'lodash/flow';
import first from 'lodash/first';
import toUpper from 'lodash/toUpper';
import find from 'lodash/find';
import now from 'lodash/now';
import random from 'lodash/random';
import forIn from 'lodash/forIn';
import findKey from 'lodash/findKey';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import isUndefined from 'lodash/isUndefined';
import lodashIsFinite from 'lodash/isFinite';
import isObject from 'lodash/isObject';
import countBy from 'lodash/countBy';
import isEqual from 'lodash/isEqual';

import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Params } from '@angular/router';

import { ISelectedRange } from 'phx-core/lib/date-picker/utils/types';
import { IDateRange } from './state-manager';

import { DashboardSettings } from '../services/dashboard-settings';

import { FUEL_ECONOMY_UNIT } from '../services/company.interface';

import { moment, IMoment } from './moment';
import { setCookie } from './cookies';
import { CookieKey } from '@lib/cookie-keys.const';
import { isAnyLocal, isPreview, isStaging, isProduction, getServerUrl as getEuOrUsServerUrl } from '@lib/environments.const';
import { CYCLES } from '../cycle/cycles';
import { FUEL_TYPES } from './fuel-types';
import { ELD_MODES } from './eld-modes';

import { ASSETS, DRIVERS, VEHICLES } from './schedule-entities';
import { PhxSelectOption } from 'phx-core/lib/select/select.types';
import { PHX_CHECKBOX_STATES } from 'phx-core';
import { logger } from './datadog';

const SERVER_URL: string = getEuOrUsServerUrl();
export const METERS_PER_KM = 1000;
export const isSafari: boolean = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

export type NzTriggerType = 'click' | 'hover';
type DefectType = 'minor' | 'major' | 'unknown' | null;
type DefectAreaType = 'tractor' | 'trailer';

export const unknownBearing: number = -99;
export type BearingDirection = 'N' | 'NE' | 'E' | 'SE'| 'S' | 'SW' | 'W' | 'NW';
export type DeviceType = 'tablet' | 'mobile' | 'desktop';

interface ITimeUnits {
  inputUnits: 'ms' | 's' | 'min';
}
export interface IMargin {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export interface ISortedDefect {
  name: string;
  isMinor: boolean;
  isMajor: boolean;
  type: DefectType;
  notes: string;
  area: DefectAreaType;
  pictureUrl: string;
  position: number;
}

export interface IPagination {
  noOfPages: number;
  pageNo: number;
  perPage: number;
  totalItems: number;
  totalItemsInLastResponse: number;
}

export type PaginationType = 'key' | 'pageNo';

export interface IServerDateRangeApiParams {
  start_date?: string;
  end_date?: string;
}

export interface IServerSortApiParams {
  sort_field?: string;
  sort_direction?: string;
}

export interface IServerPaginationApiParams {
  per_page?: number;
  page_no?: number;
}

export interface IServerPagination {
  per_page: number;
  page_no: number;
  total: number;
}

export interface IPaginatedList {
  pagination: IPagination;
}

export interface IOption {
  label: string;
  value: string;
}

export interface IAntDropdownOption {
  label: string;
  key: string;
}

export interface IUTMParams {
  utm_term?: string;
  utm_medium?: string;
  utm_source?: string;
  utm_content?: string;
  utm_campaign?: string;
}

export const UNIDENTIFIED_DRIVER_ID: number = -1;
export const IDENTIFIED_DRIVER_ID: number = -2;

export const ADA_UNAVAILABLE_MODAL_SELECTOR: string = '#ada-unavailable-modal-container';

const MILES_PER_KM: number = 0.621371;
const GALLONS_PER_LITER: number = 0.264172;
const LB_PER_KG: number = 2.20462;
const NAVIGATION_CANCELING_ERROR: string = 'ngNavigationCancelingError';
const UTM_ATTRIBUTES: string[] = ['utm_campaign', 'utm_medium', 'utm_source', 'utm_term', 'utm_content'];

export const UTM_KEY: CookieKey = CookieKey.UTM_PARAMS;
export const isIOS: boolean = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.hasOwnProperty('MSStream');
export const PHONE_MASK = ['(', /[0-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];
export const CARD_ID_MASK = Array(21).fill(/[A-Za-z,.\-\s\d]/);
export const ALLOW_ALL_MASK = Array(200).fill(/./);
export const NEW_PHONE_MASK = [/\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];

export const replace = curry(
  (pattern, replacement, str) => lodashReplace(str, pattern, replacement),
);

export const split = curry(
  (separator, str) => str.split(separator),
);

export const join = curry(
  (separator, arr) => arr.join(separator),
);

export const map = curry(
  (iteratee, collection) => lodashMap(collection, iteratee),
);

export const sortBy = curry(
  (iteratees, collection) => lodashSortBy(collection, iteratees),
);

export const wrapInObject = curry((key, value) => {
  return { [key]: value };
});

export function mapPagination(data: IServerPagination, totalItems: number): IPagination {
  return {
    perPage: data.per_page,
    totalItemsInLastResponse: totalItems,
    pageNo: data.page_no,
    totalItems: data.total,
    noOfPages: data.total === 0 ? 1 : Math.ceil(data.total / data.per_page),
  };
}

export function makeNavigationCancellingError(reason: string): Error {
  // ref: https://github.com/angular/angular/blob/4b96a58c5af01992eb0e16de500a2160d3a97fc7/packages/router/src/shared.ts#L102-L106
  const error = new Error(reason);
  (error as any)[NAVIGATION_CANCELING_ERROR] = true;
  return error;
}

export function objectToMap(obj) {
  const m = new Map();
  if (!obj) return m;
  forOwn(obj, (val, key) => m.set(key, val));
  return m;
}

export function shouldShowStandaloneDriverOnMap(eldMode: string, allowEldLogsMode: boolean = false): boolean {
  return !allowEldLogsMode ? eldMode === ELD_MODES.None : eldMode === ELD_MODES.None || eldMode === ELD_MODES.LOGS;
}

export function getDriverMovementInfoFromDutyStatus(dutyStatus: string): boolean {
  return dutyStatus === 'driving' ||
    dutyStatus === 'ym' || dutyStatus === 'pc';
}

export function mapToObj(strMap: Map<string, string>): object {
  const obj = {};
  strMap.forEach((value, key) => obj[key] = value);
  return obj;
}

export function paramsToObj(paramsMap: Params): object {
  const obj = {};
  paramsMap.forEach((value, key) => obj[key] = value);
  return obj;
}

export function getDistance(distanceInKm: number, isMetricUnits: boolean): number {
  return isMetricUnits ? distanceInKm : kmToMiles(distanceInKm);
}

export function kmToMiles(kms: number): number {
  return MILES_PER_KM * kms;
}

export function milesToKm(miles: number): number {
  return miles / MILES_PER_KM;
}

export function lbToKg(lbs: number): number {
  return lbs / LB_PER_KG;
}

export function kgToLb(kgs: number): number {
  return kgs * LB_PER_KG;
}

export function makeSingular(unit: string, val: number) {
  if (unit && unit[unit.length - 1] == 's' && (val === 0 || val === 1)) unit = unit.substr(0, unit.length - 1);
  return unit;
}

export function punctuate(words: string[]) {
  let str: string = '';
  const and: string = $localize`and`;
  words.forEach((word: string, index: number) => {
    (index === 0) ? str = word
      : str = str + ((index < words.length - 1) ? `, ${word}` : ` ${and} ${word}`);
  });
  return str;
}

export function convertDistance(fmMetricUnits: boolean, driverMetricUnits: boolean, distanceValue: number): number {
  if (fmMetricUnits && !driverMetricUnits) {
    return parseFloat(milesToKm(distanceValue).toFixed(2));
  }
  else if (!fmMetricUnits && driverMetricUnits) {
    return parseFloat(kmToMiles(distanceValue).toFixed(2));
  }

  return distanceValue;
}

export function createEntitiesCountLabel(
  driversCount: number,
  vehiclesCount: number,
  assetsCount: number,
  entity?: string | null,
  showOnly: boolean = true,
): string {
  if (!driversCount && !vehiclesCount && !assetsCount) return '';

  if ([driversCount, vehiclesCount, assetsCount]
    .filter(count => count > 0).length === 0) return $localize`No Drivers, Vehicles or Assets`;

  const entitiesLabelArray: string[] = [];
  const driverLabel: string = driversCount > 1 ? $localize`Drivers` : $localize`Driver`;
  const vehicleLabel: string = vehiclesCount > 1 ? $localize`Vehicles` : $localize`Vehicle`;
  const assetLabel: string = assetsCount == 0 ?
    $localize`No Assets` :
    assetsCount > 1 ? $localize`Assets` : $localize`Asset`;

  switch (entity) {
  case DRIVERS:
    entitiesLabelArray.push(`${driversCount} ${driverLabel}`);
    break;
  case VEHICLES:
    entitiesLabelArray.push(`${vehiclesCount} ${vehicleLabel}`);
    break;
  case ASSETS:
    entitiesLabelArray.push(assetsCount == 0 ? assetLabel : `${assetsCount} ${assetLabel}`);
    break;

  default:
    if (driversCount > 0) entitiesLabelArray.push(`${driversCount} ${driverLabel}`);
    if (vehiclesCount > 0) entitiesLabelArray.push(`${vehiclesCount} ${vehicleLabel}`);
    if (assetsCount > 0) entitiesLabelArray.push(`${assetsCount} ${assetLabel}`);
    break;
  }

  const entitiesLabel = punctuate(entitiesLabelArray);
  if (entitiesLabelArray.length === 1 && entity !== ASSETS && showOnly) {
    return entitiesLabel + $localize` only`;
  }
  return entitiesLabel;
}

export function litersToGallons(liters) {
  return GALLONS_PER_LITER * liters;
}
export function gallonsToLiters(gallons: number): number {
  return Number(gallons) / GALLONS_PER_LITER;
}

export function fullName(obj: any, appendDeactivated: boolean = false): string {
  let deactivated = '';
  if (appendDeactivated) {
    deactivated = (obj.deactivated_at || obj.deactivatedAt) ? $localize`(deactivated)` : '';
  }

  return obj ? compact([
    obj.first_name ? obj.first_name : obj.firstName,
    obj.last_name ? obj.last_name : obj.lastName,
    deactivated,
  ]).join(' ') : '';
}

export function getInitialsFromNameObject(obj: any): string {
  if (Array.isArray(obj) || !isObject(obj)) return '';

  const firstNameInitial: string = (obj.first_name || obj.firstName)?.[0]?.toUpperCase?.();
  const lastNameInitial: string = (obj.last_name || obj.lastName)?.[0]?.toUpperCase?.();

  return compact([firstNameInitial, lastNameInitial]).join('');
}

export function removeDeactivatedLabelFromEnd(str: string): string {
  return removeLabelFromString(str, $localize` (deactivated)`, true);
}

export function removeLabelFromString(str: string, label: string, startFromEnd: boolean): string {
  const lowerCaseStr = str.toLowerCase();
  const indexStart = startFromEnd ? lowerCaseStr.lastIndexOf(label) : lowerCaseStr.indexOf(label);
  if (indexStart === -1) return str;
  const substring = str.substr(indexStart, label.length);

  return str.replace(substring, '');
}

export function makeModel(obj) {
  return obj ? compact([obj.make, obj.model]).join(' ') : '';
}

export function makeModelYear(obj): string {
  return obj ? compact([obj.make, obj.model, obj.year]).join(' ') : '';
}

export function nameInitials(obj) {
  return compact([obj.first_name, obj.last_name])
    .map(flow([first, toUpper]))
    .join('')
    ;
}

export function address(obj, prefix = '') {
  if (!obj) return '';
  const stateZip = compact([obj[`${prefix}state`], obj[`${prefix}zip`]]).join(' ');
  return compact([obj[`${prefix}street`], obj[`${prefix}city`], stateZip]).join(', ');
}

export function isCanadianCycle(cycle: string): boolean {
  return cycle ? find(CYCLES, { value: cycle })?.isCanadian : false;
}

export function formatNumber(num, fractionSize = null) {
  if (fractionSize != null) {
    num = Number(num).toFixed(fractionSize);
  }
  return num.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
}

export function randomStringGenerator(length = 6) {
  let i = 0;
  let password = '';
  let rand;
  while (i < length) {
    rand = Math.floor(Math.random() * 75) + 48;
    if (rand >= 58 && rand <= 96) continue;
    i++;
    password += String.fromCharCode(rand);
  }

  return password;
}

export function randomIntInclusive(min: number, max: number): number {
  if (min > max) [min, max] = [max, min];
  return Math.floor(Math.random() * (max + 1 - min) + min);
}

export function insertAtIndex<T>(array: T[], item: T, index: number): T[] {
  return array.slice(0, index).concat([item]).concat(array.slice(index));
}

export function randomPasswordGenerator(length: number = 12, minCharClassInstance: number = 1): string {
  let password: string[] = [];

  const charClassCandidates: string[] = [
    'qwertyupasdfghjkzxcvbnm', 'QWERTYUPASDFGHJKZXCVBNM', '23456789', '@#$%*^!?+-_',
  ];
  charClassCandidates.forEach(classCandidates => {
    for (let i = 0; i < minCharClassInstance; i++) {
      const char: string = classCandidates.charAt(randomIntInclusive(0, classCandidates.length - 1));
      password = insertAtIndex(password, char, randomIntInclusive(0, password.length + 1));
    }
  });

  const allCandidates: string = charClassCandidates.join('');
  for (let i = password.length; i < length; i++) {
    const char: string = allCandidates.charAt(randomIntInclusive(0, allCandidates.length - 1));
    password = insertAtIndex(password, char, randomIntInclusive(0, password.length + 1));
  }

  return password.join('');
}

export function generateUuid(): string {
  let d = now();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (d + random(16)) % 16 | 0;
    d = Math.floor(d / 16);
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}

export function generateUniqueUuid(existingUuids: string[]): string {
  let uuid: string;
  do {
    uuid = generateUuid();
  } while (existingUuids.includes(uuid));
  return uuid;
}

const DEMO_USERS = [
  'demo_driver@gomotive.com',
  'demo_dispatcher@gomotive.com',
];

export function isDemoUser(email) {
  return DEMO_USERS.indexOf(email) >= 0;
}

export function currentQuarter() {
  const _now = moment();
  const index = Math.floor(_now.month() / 3);
  const start = _now.clone().month(index * 3).startOf('month');
  const end = start.clone().add(2, 'months').endOf('month');
  return moment().range(start, end);
}

export function createSemanticDropdownOptions(object) {
  const holder = document.createElement('div');
  forIn(object, (value, key) => {
    const item = document.createElement('div');
    item.innerText = value;
    item.setAttribute('data-value', key);
    item.setAttribute('class', 'item');
    holder.appendChild(item);
  });
  return holder.children;
}

export function createAntDropdownOptions(object): IAntDropdownOption[] {
  const options: IAntDropdownOption[] = [];
  Object.keys(object).forEach(obj =>
    options.push({ label: object[obj], key: obj }),
  );
  return options;
}

export function createPhoenixDropdownOptions(object): PhxSelectOption[] {
  const options: PhxSelectOption[] = [];
  Object.keys(object).forEach(obj =>
    options.push({ label: object[obj], value: obj }),
  );
  return options;
}

export function fuelTypeDropdownOptions() {
  return createSemanticDropdownOptions(FUEL_TYPES);
}

export function fuelTypeAntDropdownOptions() {
  return createAntDropdownOptions(FUEL_TYPES);
}

export function fuelTypePhoenixDropdownOptions() {
  return createPhoenixDropdownOptions(FUEL_TYPES);
}

export function defaultCycleDates(cycleDays) {
  const start = moment().subtract(cycleDays, 'days');
  const end = moment();

  return moment().range(start, end);
}

export function defaultCycleDatesPhx(cycleDays): ISelectedRange {
  const start = startOfDay(subDays(new Date(), cycleDays));
  const end = endOfDay(new Date());

  return { start, end };
}

export function getNestedObjectKeyFromValue(name, object) {
  let _key;
  forOwn(object, (val, key) => {
    _key = findKey(val, v => v === name);
    if (_key) return false;
  });
  return _key;
}

export function percentage(quantityA, quantityB) {
  const a = parseFloat(quantityA);
  const b = parseFloat(quantityB);
  return (a / (a + b)) * 100;
}

export function formattedPercentage(quantityA, quantityB) {
  if (quantityA == null || quantityB == null || (quantityA + quantityB === 0)) return null;
  const percent = percentage(quantityA, quantityB);
  return `${Math.round(percent)}%`;
}

export function mapEventStatus(status) {
  switch (status) {
  case 'interrupted':
    return $localize`Interrupted`;
  case 'in_progress':
    return $localize`In Progress`;
  case 'complete':
    return $localize`Complete`;
  }
}

export function getCustomEventObj(eventName, useBubble, useCapture, data) {
  const event = document.createEvent('CustomEvent');
  event.initCustomEvent(eventName, useBubble, useCapture, data);
  return event;
}

export function mapDrivingEventType(type) {
  switch (type) {
  case 'pc':
    return $localize`Personal Conveyance`;
  case 'ym':
    return $localize`Yard Move`;
  case 'driving':
    return $localize`Driving`;
  }
}

export function currentRouterPath(routerState) {
  let children = routerState.snapshot.root.children[0];
  let route = '';
  do {
    const path = children.routeConfig.path;
    if (path) route += `/${path}`;
    children = children.children[0];
  } while (children);

  return route;
}

export function parameterize(str) {
  if (str) {
    return str.toString().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  }
}

function _locationDistanceInPx(loc1, loc2, path, mapInstance) {
  const LatLng = google.maps.LatLng;
  const latPath = `${path}lat`;
  const lngPath = `${path}lng`;
  const loc1Lat = get(loc1, latPath);
  const loc1Lng = get(loc1, lngPath);
  const loc2Lat = get(loc2, latPath);
  const loc2Lng = get(loc2, lngPath);
  const p1 = mapInstance.getProjection().fromLatLngToPoint(new LatLng(loc1Lat, loc1Lng));
  const p2 = mapInstance.getProjection().fromLatLngToPoint(new LatLng(loc2Lat, loc2Lng));
  const pixelSize = Math.pow(2, -mapInstance.getZoom());
  const d = Math.sqrt((p1.x - p2.x) * (p1.x - p2.x) + (p1.y - p2.y) * (p1.y - p2.y)) / pixelSize;
  return d;
}

export function displayMarkerAccordingToZoom(mapInstance, locations, path: string = '', saturation: number = 32) {
  // limit in px
  const markerSaturation = limit => {
    const lastLocIndex = locations.length - 1;
    const visibleLocations = locations[lastLocIndex] ? [locations[lastLocIndex]] : [];
    locations.reduce((lastVisibleLocation, currentLocation, index) => {
      if (index === 0 || index === lastLocIndex) {
        visibleLocations.push(currentLocation);
        currentLocation.visible = true;
        return currentLocation;
      }
      const distance = _locationDistanceInPx(lastVisibleLocation, currentLocation, path, mapInstance);
      let isVisible = distance > limit;
      if (isVisible) {
        visibleLocations.every(loc => {
          const previousVisibleLocDistance = _locationDistanceInPx(loc, currentLocation, path, mapInstance);
          isVisible = previousVisibleLocDistance > limit;
          return isVisible;
        });
      }
      currentLocation.visible = isVisible;
      if (isVisible) {
        visibleLocations.push(currentLocation);
        return currentLocation;
      } else {
        return lastVisibleLocation;
      }
    }, locations[0]);
  };

  markerSaturation(saturation);
}

export function sortDefects(_defects, sortTypeNone) {
  let none = [];
  let major = [];
  let minor = [];
  let sortByPosition = true;
  const defects = _defects.map(d => {
    if (!d.defect) d = { defect: d };
    sortByPosition = d.defect.position;
    return {
      name: d.defect.category,
      isMinor: d.defect.type === 'minor',
      isMajor: d.defect.type === 'major',
      type: d.defect.type,
      notes: d.defect.notes,
      area: d.defect.area,
      pictureUrl: d.defect.pictureUrl,
      position: d.defect.position,
    };
  });
  lodashMap(defects, defect => {
    if (defect.isMinor) {
      minor.push(defect);
    } else if (defect.isMajor || !sortTypeNone) {
      major.push(defect);
    } else if (sortTypeNone && !defect.type) {
      none.push(defect);
    }
  });
  const sortField = sortByPosition ? 'position' : 'name';
  minor = lodashSortBy(minor, sortField);
  major = lodashSortBy(major, sortField);
  none = lodashSortBy(none, sortField);
  return [].concat(major, minor, none);
}

/**
 * Calculates fuel efficiency in units based on user preference for metric and mpg for imperial.
 * Expects distance and fuel to always be metric values (km and liters, respectively).
 */
export function calculateFuelEfficiency(
  distance: number, idleFuel: number, drivingFuel: number, metricUnits: boolean, fuelEconomyUnit: string,
) {
  let efficiency: number;
  if (metricUnits) {
    if (drivingFuel === 0 && idleFuel > 0 && distance > 0) return null;
    else {
      if (fuelEconomyUnit === FUEL_ECONOMY_UNIT.KM_PER_LITRE) efficiency = (distance / (idleFuel + drivingFuel));
      else efficiency = (((idleFuel + drivingFuel) * 100) / distance);
    }
  } else {
    if (distance >= 0 && drivingFuel === 0) return null;
    else efficiency = (kmToMiles(distance) / litersToGallons(idleFuel + drivingFuel));
  }
  return lodashIsFinite(efficiency) ? efficiency : null;
}

export function getDirectionFromBearing(bearing: number): BearingDirection {
  if ((bearing >= 0 && bearing <= 22.5) || (bearing >= 337.5 && bearing <= 360)) return 'N';
  else if (bearing > 22.5 && bearing < 67.5) return 'NE';
  else if (bearing >= 67.5 && bearing <= 112.5) return 'E';
  else if (bearing > 112.5 && bearing < 157.5) return 'SE';
  else if (bearing >= 157.5 && bearing <= 202.5) return 'S';
  else if (bearing > 202.5 && bearing < 247.5) return 'SW';
  else if (bearing >= 247.5 && bearing <= 292.5) return 'W';
  else if (bearing > 292.5 && bearing < 337.5) return 'NW';
}

export async function asyncGetElement(selector, poolingTime = 500) {
  while (document.querySelector(selector) === null) {
    await new Promise<void>(resolve => setTimeout(() => {
      resolve();
    }, poolingTime));
  }
  return document.querySelector(selector);
}

export function observeElement(
  target: HTMLElement,
  predicate: (MutationRecord) => HTMLElement,
  options: MutationObserverInit,
): Promise<HTMLElement> {
  return new Promise((resolve: (HTMLElement) => void) => {
    const mutationObserver: MutationObserver = new MutationObserver((mutations: MutationRecord[]) => {
      for (const mutation of mutations) {
        const elm: HTMLElement = predicate(mutation);
        if (elm) {
          mutationObserver.disconnect();
          resolve(elm);
        }
      }
    });
    mutationObserver.observe(target, options);
  });
}

export function createEvent(eventName: string): Event {
  let event: Event;
  if (typeof (Event) === 'function') {
    event = new Event(eventName);
  } else {
    event = document.createEvent('Event');
    event.initEvent(eventName, true, true);
  }
  return event;
}

export function createKeyboardEvent(eventName: string, params: KeyboardEventInit): KeyboardEvent {
  let event: KeyboardEvent;
  if (typeof (Event) === 'function') {
    event = new KeyboardEvent(eventName, params);
  } else {
    event = document.createEvent('KeyboardEvent');
    (event as any).initKeyboardEvent(eventName, true, true, window, params.key, 0, '', false, 'en-US');
    (event as any).initKeyboardEvent(
      eventName,
      'bubbles' in params ? params.bubbles : true,
      'cancelable' in params ? params.cancelable : true,
      'view' in params ? params.view : window,
      'key' in params ? params.key : undefined,
      'location' in params ? params.location : 0,
      '',
      'repeat' in params ? params.repeat : false,
      'en-US',
    );
  }
  return event;
}

export function removeCommas(value) {
  return value != null ? String(value).replace(/\,/g, '') : null;
}

export function removePrefix(value: string, prefix?: string): string {
  if (prefix) {
    return value.startsWith(prefix) ? value.slice(prefix.length) : value;
  }
  return value && value.slice(1);
}

export function extractResponseBody<T>(response: HttpResponse<T>): T {
  return response.body;
}

export function retryIfServerError(error: Observable<HttpErrorResponse>, maxCount: number = 1): Observable<HttpErrorResponse> {
  let retryCount: number = 0;

  return error.pipe(
    tap((err: HttpErrorResponse): void => {
      if (retryCount >= maxCount || err.status !== 500) throw err;
      retryCount++;
    }),
  );
}

export function extractEventTargetFromChild(
  target: HTMLElement,
  targetClass: string,
  threshold: number,
): HTMLElement | null {
  let count: number = 0;
  let targetExists: boolean = false;
  while (count < threshold && !targetExists) {
    targetExists = target.classList.contains(targetClass);
    if (!targetExists) {
      target = target.parentElement;
      count++;
    }
  }
  return targetExists ? target : null;
}

export function copyValueToClipboard(valueToCopy: string): void {
  const el = document.createElement('textarea');
  el.value = valueToCopy;
  document.body.appendChild(el);
  el.select();
  document.execCommand('copy');
  document.body.removeChild(el);
}

export function simulateMouseEvent($event: MouseEvent, eventName: string, targetElement: HTMLElement): void {
  $event.stopImmediatePropagation();
  $event.preventDefault();
  let event: MouseEvent;
  if (typeof MouseEvent === 'function') {
    event = new MouseEvent(eventName, {
      bubbles: true,
      cancelable: true,
      view: window,
    });
  } else {
    event = document.createEvent('MouseEvent');
    event.initMouseEvent(eventName, true, true, window, 0, 0, 0, 0, 0, false, false, false, false, 0, null);
  }
  targetElement.dispatchEvent(event);
}

export function getTimeInUserTimeZone(dateTime: any, userTz: string): Date | null {
  if (!moment.isMoment(dateTime)) return null;
  const userTimeZone: IMoment = dateTime.clone().mapTz(userTz);
  const totalOffset: number = (userTimeZone.utcOffset() / 60) + (new Date().getTimezoneOffset() / 60);
  return new Date(dateTime.toDate().setHours(dateTime.toDate().getHours() + totalOffset));
}

export function logd(...info) {
  console.debug(...info);
}

export function isTouchDevice() {
  try {
    document.createEvent('TouchEvent');
    return true;
  } catch (e) {
    return false;
  }
}

export function refreshPage(url: string = null) {
  if (url) window.location.href = url;
  else window.location.reload();
}

export const reloadApp: () => void = () => {
  window.location.reload();
  window.location.href = '/';
};


export function isNullOrUndefined(value: any): boolean {
  return isNil(value) || isUndefined(value);
}

export async function downloadFromUrl(url: string, fileName?: string, toast?: (err: string) => void ): Promise<void> {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to download file. Status: ${response.status} ${response.statusText}`);
    }

    downloadFile(url, fileName);
  } catch (error) {
    logger.error(error);
    if (toast) {
      toast($localize`Failed to download file. Please try again.`);
    }
  }
}

function downloadFile(url: string, fileName?: string): void {
  const anchor: HTMLAnchorElement = document.createElement('a');
  anchor.setAttribute('href', url);
  anchor.setAttribute('download', fileName || '');
  anchor.click();
  anchor.remove();
}

export function downloadBlob(data: Blob | string, fileName: string, contentType: string): void {
  const file: Blob = new Blob([data], { type: contentType });
  downloadFile(URL.createObjectURL(file), fileName);
}

export function getLanguageSettingModalOptions(dashboardSettings: DashboardSettings): object {
  return {
    title: $localize`Language settings`,
    cancelButtonText: '',
    showCancelButton: false,
    approveButtonText: $localize`OK`,
    isLocaleSetting: true,
    dashboardSettings,
  };
}

export function hidePendoIcon(flag: boolean) {
  const pendoIconElement: HTMLElement = document.querySelector('.pendo-block-wrapper');
  if (pendoIconElement) pendoIconElement.style.display = flag ? 'none' : 'initial';
}

export function mapValueToBoolean(value: string | boolean): boolean {
  if (typeof value === 'string') return value == 'true';
  return value;
}

export function isPhxCheckboxEnabled(key: string): boolean {
  return key === PHX_CHECKBOX_STATES.ON;
}

export function getPhxCheckboxState(value: boolean): string {
  return value ? PHX_CHECKBOX_STATES.ON : PHX_CHECKBOX_STATES.OFF;
}

export function isEmptyOrSpaces(value: string): boolean {
  return (!value || (value || '').trim().length === 0);
}

export function saveUTMParamsToCookies(queryParams: object): void {
  const utmData: object = {};
  const domainUrl: string = window.location.protocol + '//' + window.location.host;

  UTM_ATTRIBUTES.forEach((attribute: string): void => {
    if (queryParams.hasOwnProperty(attribute)) utmData[attribute] = queryParams[attribute];
  });

  if (Object.keys(utmData).length > 0) {
    setCookie(UTM_KEY, JSON.stringify(utmData), { expires: 30, domain: `.${getPrimaryDomain(domainUrl)}` });
  }
}

export function toMoment(date: string): IMoment {
  if (!date) return;

  return moment(date);
}

export function toMomentUTC(date: string): IMoment {
  if (!date) return;

  return moment.utc(date);
}

export function toMomentStartOfDay(date: string): IMoment {
  return toMoment(date)?.startOf('day') as IMoment;
}

export function toUserTimeZone(date: string, userTimeZone: string): IMoment {
  if (!date || !userTimeZone) return;

  return moment(date).mapTz(userTimeZone);
}

export function calcPercentage(numerator: number, denominator: number): number {
  if (numerator == null || denominator == null || (numerator + denominator) === 0) return;

  return Math.round((numerator/denominator) * 100);
}

export function upperClamp(value: number, maxValue: number): number {
  return maxValue ? clamp(value, Number.MIN_VALUE, maxValue) : value;
}

export function lowerClamp(value: number, minValue: number): number {
  return minValue ? clamp(value, minValue, Number.MAX_VALUE) : value;
}

export function trimString(text: string): string {
  // remove newlines and trailing spaces
  return text.replace(/\r?\n|\r/g,'').trim();
}

export function scheduleAsyncTask(callback: () => void, timeInMilliseconds: number = 0): void {
  const asyncScheduleSubscription = asyncScheduler.schedule(() => {
    callback();
    asyncScheduleSubscription.unsubscribe();
  }, timeInMilliseconds);
}

export function oldestSelectableDate(oldestYear = 2) {
  return moment().subtract(oldestYear, 'years').startOf('day');
}

export function getServerUrl() {
  return SERVER_URL.split('//')[1];
}

export function openLinkInNewTab(link: string): void {
  window.open(link, '_blank');
}

export function transformToNativeDateRange(range: IDateRange): ISelectedRange {
  if (range?.end.isValid()) {
    return {
      start: range.start.toDate(),
      end: range.end.toDate(),
    };
  } else if (range?.start.isValid()) {
    return {
      start: range.start.toDate(),
    };
  }
  return null;
}

export function transformToNativeDate(date: IMoment): Date {
  if (date?.isValid()) {
    return date?.toDate();
  }
  return null;
}

export function transformToMomentRange(range: ISelectedRange): IDateRange {
  if (range !== null) {
    const { start = null, end = null } = range;
    return {
      start: moment(start),
      end: moment(end),
    };
  }
  return null;
}

export function transformToMomentDateTimeRange(range: ISelectedRange): IDateRange {
  if (range !== null) {
    const { start = null, end = null } = range;
    return {
      start: moment(start)?.startOf('day'),
      end: moment(end)?.endOf('day'),
    };
  }
  return null;
}

export const SERVER_DATE_FORMAT: string = 'yyyy-MM-dd';
export const USER_DATE_FORMAT: string = 'MMM dd, yyyy';

export function getPrimaryDomain(url: string): string {
  const { hostname }: { hostname: string } = new URL(url);
  const domainParts: string[] = hostname.split('.');
  const primaryDomain: string = domainParts.slice(-2).join('.');

  return primaryDomain;
}

export function getDeviceType(): DeviceType {
  const isTabletWidth = window.matchMedia('(min-width: 768px) and (max-width: 1024px)').matches;
  const isMobileWidth = window.matchMedia('(max-width: 767px)').matches;

  const userAgent: string = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(userAgent) && isTabletWidth) {
    return 'tablet';
  } else if (
    /Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/
    .test(userAgent) && isMobileWidth
  ) {
    return 'mobile';
  }
  return 'desktop';
}

export function lowerCaseFirstLetterOfWords(inputString) {
  const words = inputString.split(' ');

  for (let i = 1; i < words.length; i++) {
    words[i] = words[i].charAt(0).toLowerCase() + words[i].slice(1);
  }

  return words.join(' ');
}

export function sanitizeFileName(fileName: string): string {
  return fileName.trim().replace(/[#%&{}\\<>?=|*/. $!'":@+`]/g, '');
}

export function getShopUrl(): string {
  let shopUrl: string = '';

  if (isPreview() || isAnyLocal()) shopUrl = 'https://shop.qa.k2labs.org/';
  else if (isStaging()) shopUrl = 'https://shop.uat.k2labs.org';
  else if (isProduction()) shopUrl = 'https://shop.gomotive.com';

  return shopUrl;
}

export function phoneNumberToIntlFormat(input: string, countryCode?: string): string {
  const rawPhoneNumber = phoneNumberPreProcess(input);

  // Format the digits into the desired format (XXX-XXX-XXXX)
  const formatted = rawPhoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');

  if (countryCode) return `${countryCode} ${formatted}`;

  return formatted;
}

export function phoneNumberToStandardFormat(input: string): string {
  const rawPhoneNumber = phoneNumberPreProcess(input);

  // Format the digits into the desired format (XXX) XXX-XXXX
  const formatted = rawPhoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');

  return formatted;
}

function phoneNumberPreProcess(input: string) {
  if (!input) return '';

  // Remove any non-digit characters
  return rawPhoneFormat(input);
}

export function xor(alert: boolean, event: boolean): boolean {
  return (alert || event) && !(alert && event);
}

export function rawPhoneFormat(phone: string): string {
  return phone?.replace(/\D/g, '');
}

// Converts URL text into clickable HTML link using javaScript
export function linkify(inputText: string): string {
  let replacedText: string;
  const targetBlank: string = 'target="_blank" rel="noopener noreferrer"';

  // URLs starting with http://, https://, or ftp://
  const replacePattern1 = /(\b(https?|ftp):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gim;
  replacedText = inputText.replace(replacePattern1, `<a href="$1" ${targetBlank}>$1</a>`);

  // URLs starting with www. (without // before it, or it'd re-link the ones done above)
  const replacePattern2 = /(^|[^\/])(www\.[\S]+(\b|$))/gim;
  replacedText = replacedText.replace(replacePattern2, `$1<a href="http://$2" ${targetBlank}">$2</a>`);

  // URLs starting with out www. like google.com
  const replacePattern3 = /(^|\s)([a-zA-Z0-9_\-\.]+\.[\w]+(?!\S))/gim;
  replacedText = replacedText.replace(replacePattern3, `$1<a href="http://www.$2" ${targetBlank}">$2</a>`);

  // Change email addresses to mailto:: links
  const replacePattern4 = /(([\S]+)@[a-zA-Z_]+?(?:\.[a-zA-Z]{2,6}))+/gim;
  replacedText = replacedText.replace(replacePattern4, '<a href="mailto:$1">$1</a>');

  return replacedText;
}

export function getUrlParams(url: string): { [key: string]: string } {
  let hash: string[];
  const params: { [key: string]: string } = {};
  const hashes: string[] = url.slice(url.indexOf(';') + 1).split(';');
  for (const value of hashes) {
    hash = value.split('=');
    params[hash[0]] = hash[1];
  }
  return params;
}

export function getQueryParamsFromRelativeUrl(relativeUrl: string): Record<string, string> {
  const queryIndex = relativeUrl.indexOf('?');
  if (queryIndex === -1) return {};

  const queryString = relativeUrl.slice(queryIndex + 1);
  return Object.fromEntries(new URLSearchParams(queryString));
}

export function convertToSentenceCase(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

export function removeEmptyPropertiesFromObject(obj) {
  return Object.fromEntries(
    Object.entries(obj)
      .filter(([_, v]) => v != null)
      .map(([k, v]) => [k, v === Object(v) ? removeEmptyPropertiesFromObject(v) : v]),
  );
}

/**
 * Checks if two arrays have the same values. Only works for primitive types.
 * For unordered comparison, the order of values in the arrays is ignored.
 */
export function areArrayValuesEqual<T>(firstArray: T[], secondArray: T[], unordered: boolean = false): boolean {
  if (firstArray.length !== secondArray.length) return false;

  if (unordered) {
    const firstMap = countBy(firstArray);
    const secondMap = countBy(secondArray);
    return isEqual(firstMap, secondMap);
  }

  return firstArray.every((value: T, index: number) => value === secondArray[index]);
}

export type Mask = any; // false | (string | RegExp)[] | ((raw: string) => (string | RegExp)[]);

/**
 * Converts a snake_case string to camelCase.
 *
 * @param str - The snake_case string to be converted.
 * @returns The converted camelCase string.
 */
export function snakeCaseToCamelCase(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

/**
 * Converts a numeric time duration into a human-readable string format.
 *
 * The input time can be in milliseconds, seconds, or minutes, and will be
 * normalized to milliseconds before formatting. The result is a string like:
 * - "45 sec" (for durations less than a minute)
 * - "2 min 15 sec" (for durations under an hour)
 * - "1 hr 5 min 30 sec" (for durations over an hour)
 *
 * @param timeValue - The duration value to be converted.
 * @param inputUnits - An object specifying the unit of the input value ('ms', 's', or 'min').
 * @returns A human-readable duration string.
 */
export const transformDuration = (timeValue: number, inputUnits: ITimeUnits): string => {
  if (isNaN(timeValue)) return `0 ${$localize`sec`}`;

  // Convert to milliseconds
  let durationMs = timeValue;
  switch (inputUnits.inputUnits) {
  case 's':
    durationMs = timeValue * 1000;
    break;
  case 'min':
    durationMs = timeValue * 60 * 1000;
    break;
  case 'ms':
  default:
    break;
  }

  const totalSeconds = durationMs / 1000;
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const parts: string[] = [];

  if (hours > 0) {
    parts.push(hours === 1
      ? `${hours} ${$localize`hr`}`
      : `${hours} ${$localize`hrs`}`);
  }

  if (minutes > 0) {
    parts.push(minutes === 1
      ? `${minutes} ${$localize`min`}`
      : `${minutes} ${$localize`mins`}`);
  }

  if (seconds > 0 || parts.length === 0) {
    parts.push(seconds === 1
      ? `${seconds} ${$localize`sec`}`
      : `${seconds} ${$localize`secs`}`);
  }

  return parts.join(' ');
};

export function instanceOfPagination(obj: any): obj is IPagination {
  return (typeof obj === 'object') &&
    ('noOfPages' in obj && typeof obj.noOfPages === 'number') &&
    ('pageNo' in obj && typeof obj.pageNo === 'number') &&
    ('perPage' in obj && typeof obj.perPage === 'number') &&
    ('totalItems' in obj && typeof obj.totalItems === 'number') &&
    ('totalItemsInLastResponse' in obj && typeof obj.totalItemsInLastResponse === 'number');
}

export function convertUtcDateStringToUserTime(
  utcDateStr: string,
  userTimeZone: string
): string {
  const date = new Date(utcDateStr);

  const formatted = date.toLocaleString("en-US", {
    timeZone: userTimeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return formatted.replace(",", "") || '';
}

export const formattedNumber = (num: number | string): string => {
  if (num === null || num === undefined || num === '') return '';
  const value = typeof num === 'string' ? Number(num) : num;
  if (isNaN(value)) return '';
  return new Intl.NumberFormat('en-US').format(value);
};
